import React, { useState, useEffect, useCallback } from "react";
import { authAxios, API } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Package, Search } from "lucide-react";
import MediaUploadInput from "@/components/MediaUploadInput";
import GroupsMultiSelect from "@/components/GroupsMultiSelect";

const EMPTY = {
  name: "", description: "", price: 0, currency: "USD",
  image_url: "", category: "", stock: 0, sku: "", is_active: true,
  variations: [], group_chat_ids: [],
};
const EMPTY_VARIATION = { label: "", price: 0, currency: "", sku: "", stock: 0 };

export default function CatalogPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const fetchItems = useCallback(async () => {
    try {
      const { data } = await authAxios.get(`${API}/products`);
      setItems(data);
    } catch (e) { /* noop */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (p) => { setEditing(p.id); setForm({ ...EMPTY, ...p }); setOpen(true); };

  const save = async () => {
    if (!form.name.trim()) { alert("Nombre requerido"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        price: Number(form.price) || 0,
        stock: Number(form.stock) || 0,
        group_chat_ids: Array.isArray(form.group_chat_ids) ? form.group_chat_ids : [],
        variations: (form.variations || []).map(v => ({
          label: v.label || "",
          price: Number(v.price) || 0,
          currency: v.currency || "",
          sku: v.sku || "",
          stock: Number(v.stock) || 0,
        })).filter(v => v.label.trim().length > 0),
      };
      if (editing) await authAxios.put(`${API}/products/${editing}`, payload);
      else await authAxios.post(`${API}/products`, payload);
      setOpen(false);
      fetchItems();
    } catch (e) {
      alert(e.response?.data?.detail || "No se pudo guardar");
    } finally { setSaving(false); }
  };

  const addVariation = () => setForm({ ...form, variations: [...(form.variations || []), { ...EMPTY_VARIATION }] });
  const removeVariation = (i) => setForm({ ...form, variations: form.variations.filter((_, idx) => idx !== i) });
  const updateVariation = (i, field, value) => {
    const vs = [...(form.variations || [])];
    vs[i] = { ...vs[i], [field]: value };
    setForm({ ...form, variations: vs });
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este producto?")) return;
    await authAxios.delete(`${API}/products/${id}`);
    fetchItems();
  };

  const toggle = async (p) => {
    await authAxios.put(`${API}/products/${p.id}`, { ...p, is_active: !p.is_active });
    fetchItems();
  };

  const filtered = items.filter(p => {
    if (!query) return true;
    const q = query.toLowerCase();
    return (p.name || "").toLowerCase().includes(q) || (p.category || "").toLowerCase().includes(q);
  });

  return (
    <div className="p-8 max-w-7xl mx-auto" data-testid="catalog-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight flex items-center gap-2">
            <Package className="w-7 h-7" /> Catálogo de Productos
          </h1>
          <p className="text-slate-500 mt-1">Administra productos y servicios del bot (.catalogo / .stock)</p>
        </div>
        <Button onClick={openNew} data-testid="catalog-new-btn">
          <Plus className="w-4 h-4 mr-1" /> Nuevo Producto
        </Button>
      </div>

      <div className="mb-4 relative max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
        <Input
          className="pl-9"
          placeholder="Buscar producto o categoría..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          data-testid="catalog-search"
        />
      </div>

      {loading ? (
        <div className="animate-pulse h-40 bg-slate-200 rounded-xl" />
      ) : filtered.length === 0 ? (
        <Card><CardContent className="p-12 text-center text-slate-500">
          <Package className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p className="mb-3">{items.length === 0 ? "Aún no hay productos." : "Sin resultados."}</p>
          {items.length === 0 && <Button onClick={openNew} variant="outline">Crear el primer producto</Button>}
        </CardContent></Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map(p => (
            <Card key={p.id} className="overflow-hidden" data-testid={`product-card-${p.id}`}>
              {p.image_url ? (
                <img src={p.image_url} alt={p.name} className="w-full h-40 object-cover" />
              ) : (
                <div className="w-full h-40 bg-slate-100 flex items-center justify-center">
                  <Package className="w-10 h-10 text-slate-300" />
                </div>
              )}
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <h3 className="font-semibold truncate">{p.name}</h3>
                    {p.category && <Badge variant="secondary" className="mt-1 text-xs">{p.category}</Badge>}
                  </div>
                  <Switch checked={!!p.is_active} onCheckedChange={() => toggle(p)} data-testid={`product-toggle-${p.id}`} />
                </div>
                {p.description && <p className="text-sm text-slate-500 mt-2 line-clamp-2">{p.description}</p>}
                {Array.isArray(p.variations) && p.variations.length > 0 ? (
                  <div className="mt-3 space-y-1 bg-slate-50 rounded p-2">
                    {p.variations.slice(0, 4).map((v, i) => (
                      <div key={i} className="flex justify-between text-xs">
                        <span className="text-slate-700">{v.label}</span>
                        <span className="font-mono">{v.currency || p.currency || "USD"} {(Number(v.price) || 0).toFixed(2)}</span>
                      </div>
                    ))}
                    {p.variations.length > 4 && <p className="text-xs text-slate-400">+ {p.variations.length - 4} más</p>}
                  </div>
                ) : (
                  <div className="flex items-center justify-between mt-3">
                    <span className="font-mono text-sm">${(Number(p.price) || 0).toFixed(2)} {p.currency}</span>
                    <span className="text-xs text-slate-500">Stock: {p.stock ?? 0}</span>
                  </div>
                )}
                <div className="flex gap-2 mt-3">
                  <Button size="sm" variant="outline" className="flex-1" onClick={() => openEdit(p)} data-testid={`product-edit-${p.id}`}>
                    <Pencil className="w-3 h-3 mr-1" /> Editar
                  </Button>
                  <Button size="sm" variant="outline" className="text-red-600" onClick={() => remove(p.id)} data-testid={`product-delete-${p.id}`}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="product-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar Producto" : "Nuevo Producto"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Nombre *</Label>
              <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="product-name" />
            </div>
            <div><Label>Descripción</Label>
              <Textarea rows={3} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} data-testid="product-desc" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Precio</Label>
                <Input type="number" step="0.01" value={form.price} onChange={e => setForm({ ...form, price: e.target.value })} data-testid="product-price" />
              </div>
              <div><Label>Moneda</Label>
                <Input value={form.currency} onChange={e => setForm({ ...form, currency: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Stock</Label>
                <Input type="number" value={form.stock} onChange={e => setForm({ ...form, stock: e.target.value })} data-testid="product-stock" />
              </div>
              <div><Label>Categoría</Label>
                <Input value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} />
              </div>
            </div>
            <div><Label>SKU</Label>
              <Input value={form.sku} onChange={e => setForm({ ...form, sku: e.target.value })} />
            </div>
            <div><Label>URL Imagen</Label>
              <MediaUploadInput
                value={form.image_url}
                onChange={(v) => setForm({ ...form, image_url: v })}
                accept="image/*"
                placeholder="https://... o sube una imagen"
                testIdPrefix="product-image"
              />
              <p className="text-xs text-slate-500 mt-1">Sube una imagen o pega el URL. Se almacena en Media Library.</p>
            </div>
            <div className="flex items-center justify-between">
              <Label>Activo</Label>
              <Switch checked={!!form.is_active} onCheckedChange={v => setForm({ ...form, is_active: v })} />
            </div>

            {/* Groups filter — limit catalog/stock visibility to chosen groups */}
            <div className="border-t pt-3">
              <GroupsMultiSelect
                value={form.group_chat_ids || []}
                onChange={(ids) => setForm({ ...form, group_chat_ids: ids })}
                label="Grupos donde aparece este producto (vacío = todos)"
                helpText="Si eliges grupos, .catalogo / .stock / .pedido sólo mostrarán este producto en esos grupos. En privado siempre se muestra."
                testIdPrefix="product-groups"
              />
            </div>

            {/* Variations */}
            <div className="border-t pt-3">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <Label className="text-base">📋 Variaciones</Label>
                  <p className="text-xs text-slate-500">Ej. 1 mes / 3 meses / 1 usuario / 5 usuarios. Cada una con su propio precio.</p>
                </div>
                <Button type="button" size="sm" variant="outline" onClick={addVariation} data-testid="product-add-variation">
                  <Plus className="w-3 h-3 mr-1" /> Agregar
                </Button>
              </div>
              {(form.variations || []).length === 0 ? (
                <p className="text-xs text-slate-400 italic">Sin variaciones. El producto se vende con el precio base.</p>
              ) : (
                <div className="space-y-2">
                  {form.variations.map((v, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 items-center bg-slate-50 p-2 rounded" data-testid={`variation-row-${i}`}>
                      <Input
                        placeholder="1 mes / 3 usuarios"
                        value={v.label}
                        onChange={e => updateVariation(i, "label", e.target.value)}
                        className="col-span-5 h-8 text-sm"
                        data-testid={`variation-label-${i}`}
                      />
                      <Input
                        placeholder="Precio"
                        type="number" step="0.01"
                        value={v.price}
                        onChange={e => updateVariation(i, "price", e.target.value)}
                        className="col-span-3 h-8 text-sm"
                        data-testid={`variation-price-${i}`}
                      />
                      <Input
                        placeholder="Stock"
                        type="number"
                        value={v.stock}
                        onChange={e => updateVariation(i, "stock", e.target.value)}
                        className="col-span-3 h-8 text-sm"
                        data-testid={`variation-stock-${i}`}
                      />
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="col-span-1 text-red-600 h-8 w-8 p-0"
                        onClick={() => removeVariation(i)}
                        data-testid={`variation-remove-${i}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} disabled={saving} data-testid="product-save">
              {saving ? "Guardando..." : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
